<script setup lang="ts">
  import * as yup from 'yup'
  import { useField, useForm } from 'vee-validate'
  import { FormInput, Loading } from '@components/index'
  import { mutateLogin } from '@server/index'
  import { useUserStore } from '@/stores/userStore'
  import navigate from '@utils/navigate'
  import handleError from '@utils/errorHandler'

  const route = useRoute()

  const userStore = useUserStore()

  const loading = ref(false)
  const alreadySubmittedOnce = ref(false)

  definePageMeta({
    layout: 'auth'
  })

  const validationSchema = yup.object({
    email: yup.string().required('Email é obrigatório').email().label('Email Inválido'),
    password: yup
      .string()
      .required('Senha é obrigatória')
      .min(8, 'Senha deve ter pelo menos 6 caracteres')
      .label('Senha inválida')
  })

  const { handleSubmit, errors, validate } = useForm({
    validationSchema,
    initialValues: {
      email: '',
      password: ''
    }
  })

  const { value: emailField } = useField<string>('email')
  const { value: passwordField } = useField<string>('password')

  const submitButtonText = ref('ACESSAR')

  const loginControl = handleSubmit(async () => {
    loading.value = true
    const { execute, data, error } = mutateLogin({
      email: emailField.value,
      password: passwordField.value
    })

    try {
      await execute()
      if (error.value) {
        handleError(error.value)
      }
      if (data.value) {
        const loginData = data.value
        userStore.setUserStore(loginData.id, loginData.name, loginData.email, loginData.favorites)
        if (route.query.redirect) {
          navigate.push(route.query.redirect)
        } else {
          navigate.push(`/`)
        }
      }
    } catch (err: any) {
      handleError(err)
    } finally {
      setTimeout(() => {
        loading.value = false
      }, 1000)
    }

    setTimeout(() => {
      loading.value = false
    }, 1000)
  })

  const handleLogin = async () => {
    alreadySubmittedOnce.value = true
    await validate()
    loginControl()
  }

  watch(emailField, () => {
    errors.value.email = ''
  })

  watch(passwordField, () => {
    errors.value.password = ''
  })
</script>

<template>
  <div>
    <form
      :validation-schema="validationSchema"
      @submit.prevent="handleLogin"
    >
      <FormInput
        id="email"
        v-model="emailField"
        class="col-12 form-input-component mb-3"
        label="E-mail"
        name="email"
        type="email"
        icon="bi bi-envelope-fill"
        :is-invalid="!!errors.email"
      />
      {{ errors.email }}

      <FormInput
        id="password"
        v-model="passwordField"
        class="col-12 form-input-component mb-3"
        label="Senha"
        name="password"
        type="password"
        icon="bi bi-lock-fill"
        :is-invalid="!!errors.password"
      />
      {{ errors.password }}

      <div class="form-input-component">
        <button
          data-test-id="login-button"
          class="secondary"
        >
          {{ !loading ? submitButtonText : '' }}
          <Loading
            v-if="loading"
            class="mt-3"
            :color="'#fff'"
            :duration="'2.3s'"
          />
        </button>

        <div class="col-12 text-center mt-4 mb-5">
          <p
            class="recovery-password"
            style="color: #c3c3c6"
          >
            <u class="correct-text-color">
              <NuxtLink to="auth/recovery">Esqueceu a senha?</NuxtLink>
            </u>
          </p>
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
  .recovery-password {
    cursor: pointer;
  }
  .ou {
    text-align: center;
    margin-top: -28px;

    span {
      background-color: #000000;
      padding: 0 10px;
    }
  }
  correct-text-color {
    color: #c3c3c6;
  }
</style>
